import React, { useState, useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import {
  Calendar,
  Plus,
  Edit,
  Copy,
  Eye,
  X,
  Trash2,
  MapPin,
  Grid,
  List,
} from 'lucide-react'
import { adminPlansService, adminCategoriesService } from '../services'
import { formatPriceDisplay } from '../utils/formatPrice'
import './Plans.css'

const PLANS_CACHE_KEY = 'admin_plans_cache_v1'

const normalizePlans = (list) =>
  (list || []).map((p) => ({
    ...p,
    category: p.category || 'General',
    price: p.price ?? 0,
    capacity: p.capacity ?? 0,
    duration: p.durationMin ?? p.duration ?? 0,
    status: (p.status || 'draft').charAt(0).toUpperCase() + (p.status || 'draft').slice(1),
    activities: Array.from({ length: p.activitiesCount || 0 }, (_, i) => ({
      id: i + 1,
      name: `Activity ${i + 1}`,
      vendorName: '',
      duration: 0
    })),
  }))

const readPlansCache = () => {
  try {
    const raw = localStorage.getItem(PLANS_CACHE_KEY)
    if (!raw) return []
    const parsed = JSON.parse(raw)
    if (!Array.isArray(parsed?.items)) return []
    return parsed.items
  } catch {
    return []
  }
}

const writePlansCache = (items) => {
  try {
    localStorage.setItem(PLANS_CACHE_KEY, JSON.stringify({ items, savedAt: Date.now() }))
  } catch {
    return
  }
}

const sleep = (ms) => new Promise((resolve) => setTimeout(resolve, ms))

const fetchPlansWithRetry = async (maxRetries = 2) => {
  let lastError
  for (let attempt = 0; attempt <= maxRetries; attempt += 1) {
    try {
      return await adminPlansService.list()
    } catch (error) {
      lastError = error
      if (attempt === maxRetries) throw lastError
      await sleep((attempt + 1) * 500)
    }
  }
  throw lastError
}

const Plans = () => {
  const navigate = useNavigate()
  const [plans, setPlans] = useState([])
  const [loading, setLoading] = useState(true)
  const [viewMode, setViewMode] = useState('grid')
  const [searchTerm, setSearchTerm] = useState('')
  const [filterCategory, setFilterCategory] = useState('All')
  const [filterStatus, setFilterStatus] = useState('All')
  const [showModal, setShowModal] = useState(false)
  const [selectedPlan, setSelectedPlan] = useState(null)
  const [categories, setCategories] = useState([])

  useEffect(() => {
    let isMounted = true
    const cachedPlans = readPlansCache()
    if (cachedPlans.length > 0) {
      setPlans(cachedPlans)
      setLoading(false)
    }

    adminCategoriesService
      .list()
      .then((items) => {
        if (!isMounted) return
        setCategories(items || [])
      })
      .catch(() => {
        if (!isMounted) return
        setCategories([])
      })

    ;(async () => {
      try {
        const list = await fetchPlansWithRetry(cachedPlans.length > 0 ? 1 : 2)
        if (!isMounted) return
        const normalized = normalizePlans(list)
        setPlans(normalized)
        writePlansCache(normalized)
      } catch {
        if (!isMounted) return
      } finally {
        if (!isMounted) return
        setLoading(false)
      }
    })()

    return () => {
      isMounted = false
    }
  }, [])

  const filteredPlans = plans.filter((plan) => {
    const matchesSearch = (plan.title || '').toLowerCase().includes(searchTerm.toLowerCase())
    const matchesCategory = filterCategory === 'All' || plan.category === filterCategory
    const matchesStatus = filterStatus === 'All' || (plan.status || '').toLowerCase() === filterStatus.toLowerCase()
    return matchesSearch && matchesCategory && matchesStatus
  })

  const handleAddPlan = () => {
    navigate('/plans/new')
  }

  const handleEditPlan = (plan) => {
    navigate(`/plans/${plan.id}`, { state: { plan } });
  };

  const handleDuplicatePlan = (plan) => {
    const newPlan = {
      ...plan,
      id: `copy-${plan.id}-${Date.now()}`,
      title: `${plan.title} (Copy)`,
      status: 'draft',
      bookings: 0,
      createdDate: new Date().toISOString().split('T')[0],
    }
    setPlans((prev) => {
      const updated = [...prev, newPlan]
      writePlansCache(updated)
      return updated
    })
  }

  const handleTogglePublish = async (planId) => {
    const plan = plans.find(p => p.id === planId);
    if (!plan) return;
    const newStatus = (plan.status || '').toLowerCase() === 'published' ? 'draft' : 'published';
    try {
      await adminPlansService.update(planId, { status: newStatus });
      setPlans((prev) => {
        const updated = prev.map((p) =>
          p.id === planId ? { ...p, status: newStatus.charAt(0).toUpperCase() + newStatus.slice(1) } : p
        )
        writePlansCache(updated)
        return updated
      });
    } catch (error) {
      console.error('Failed to update plan status:', error);
      // Optionally show toast
    }
  };

  return (
    <div className="plans-page">
      <div className="page-header">
        <div className="header-titles">
          <h1>Plans Management</h1>
          <p>Create and manage curated plans for your users.</p>
        </div>
      </div>

      {/* Actions Bar */}
      <div className="page-actions">
        <div style={{ display: 'flex', gap: '1rem', flex: 1, alignItems: 'center' }}>
          <input
            type="text"
            className="search-input"
            placeholder="Search plans..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
          />
          <div className="view-mode-toggle">
            <button
              className={`view-mode-btn ${viewMode === 'grid' ? 'active' : ''}`}
              onClick={() => setViewMode('grid')}
            >
              <Grid size={16} />
              Grid
            </button>
            <button
              className={`view-mode-btn ${viewMode === 'list' ? 'active' : ''}`}
              onClick={() => setViewMode('list')}
            >
              <List size={16} />
              List
            </button>
          </div>
        </div>
        <div className="filter-group">
          <select
            className="filter-select"
            value={filterCategory}
            onChange={(e) => setFilterCategory(e.target.value)}
          >
            <option value="All">All Categories</option>
            {categories
              .filter((c) => (c.status || '').toLowerCase() === 'active')
              .map((cat) => (
                <option key={cat.id} value={cat.name}>
                  {cat.name}
                </option>
              ))}
          </select>
          <select
            className="filter-select"
            value={filterStatus}
            onChange={(e) => setFilterStatus(e.target.value)}
          >
            <option value="All">All Status</option>
            <option value="Published">Published</option>
            <option value="Draft">Draft</option>
          </select>
          <button className="btn-primary" onClick={handleAddPlan}>
            <Plus size={18} />
            Create Plan
          </button>
        </div>
      </div>

      {/* Plans Display */}
      {loading ? (
        <div className="loading-state">
          <div className="loading-spinner" />
          <span>Loading plans…</span>
        </div>
      ) : viewMode === 'grid' ? (
        <div className="plans-grid">
          {filteredPlans.map((plan) => (
            <PlanCard
              key={plan.id}
              plan={plan}
              onEdit={() => handleEditPlan(plan)}
              onDuplicate={() => handleDuplicatePlan(plan)}
              onTogglePublish={() => handleTogglePublish(plan.id)}
            />
          ))}
        </div>
      ) : (
        <div className="plans-list">
          {filteredPlans.map((plan) => (
            <PlanListItem
              key={plan.id}
              plan={plan}
              onEdit={() => handleEditPlan(plan)}
              onDuplicate={() => handleDuplicatePlan(plan)}
              onTogglePublish={() => handleTogglePublish(plan.id)}
            />
          ))}
        </div>
      )}


    </div>
  )
}

// Plan Card Component (Grid View)
const PlanCard = ({ plan, onEdit, onDuplicate, onTogglePublish }) => {
  const isPublished = (plan.status || '').toLowerCase() === 'published'
  return (
    <div className={`plan-card ${isPublished ? 'status-published' : 'status-draft'}`} onClick={onEdit}>
      <div className="plan-card-thumbnail">
        {plan.thumbnail ? (
          <img
            src={plan.thumbnail}
            alt={plan.title || 'Plan thumbnail'}
            className="plan-card-thumbnail-img"
            onError={(e) => {
              e.currentTarget.style.display = 'none';
              const parent = e.currentTarget.parentElement;
              if (parent) parent.classList.add('has-placeholder');
            }}
          />
        ) : (
          <div className="plan-card-placeholder">
            <div className="placeholder-icon" aria-hidden="true">
              <svg
                width="32"
                height="32"
                viewBox="0 0 32 32"
                xmlns="http://www.w3.org/2000/svg"
              >
                <defs>
                  <linearGradient id="planIconGradient" x1="0" y1="0" x2="1" y2="1">
                    <stop offset="0%" stopColor="#22c55e" />
                    <stop offset="100%" stopColor="#14b8a6" />
                  </linearGradient>
                </defs>
                <rect
                  x="5"
                  y="7"
                  width="18"
                  height="20"
                  rx="3"
                  fill="#ffffff"
                  stroke="url(#planIconGradient)"
                  strokeWidth="1.5"
                />
                <rect
                  x="9"
                  y="5"
                  width="18"
                  height="20"
                  rx="3"
                  fill="url(#planIconGradient)"
                  opacity="0.12"
                />
                <circle cx="13" cy="12" r="1.4" fill="#22c55e" />
                <rect x="16" y="11.2" width="7" height="1.6" rx="0.8" fill="#9ca3af" />
                <circle cx="13" cy="16" r="1.4" fill="#22c55e" />
                <rect x="16" y="15.2" width="6" height="1.6" rx="0.8" fill="#9ca3af" />
                <circle cx="13" cy="20" r="1.4" fill="#22c55e" />
                <rect x="16" y="19.2" width="5" height="1.6" rx="0.8" fill="#9ca3af" />
              </svg>
            </div>
          </div>
        )}
      </div>
      <div className="plan-card-header">
        <h3 className="plan-card-title">{plan.title}</h3>
        <span className="plan-card-category">{plan.category}</span>
      </div>
      <div className="plan-card-body">
        <div className="plan-activities-row">
          <div className="stacked-avatars">
            {(plan.activities || []).slice(0, 4).map((_, index) => (
              <div key={index} className="stacked-avatar" style={{ zIndex: 4 - index }}>
                {index + 1}
              </div>
            ))}
            {(plan.activities || []).length > 4 && (
              <div className="stacked-avatar stacked-avatar-overflow">
                +{(plan.activities || []).length - 4}
              </div>
            )}
          </div>
          <span className="activities-count-label">
            {(plan.activities || []).length} {(plan.activities || []).length === 1 ? 'Activity' : 'Activities'}
          </span>
        </div>
      </div>
      <div className="plan-card-footer">
        <div className="plan-stat">
          <div className="plan-stat-value">{formatPriceDisplay(plan.price)} EGP</div>
          <div className="plan-stat-label">Price</div>
        </div>
        <div className="plan-stat">
          <div className="plan-stat-value">{plan.capacity}</div>
          <div className="plan-stat-label">Capacity</div>
        </div>
        <div className="plan-stat">
          <div className="plan-stat-value">{plan.bookings}</div>
          <div className="plan-stat-label">Bookings</div>
        </div>
      </div>
      <div className="plan-card-actions" onClick={(e) => e.stopPropagation()}>
        <div className="plan-publish-toggle">
          <label className="toggle-switch">
            <input
              type="checkbox"
              checked={plan.status === 'Published'}
              onChange={onTogglePublish}
            />
            <span className="toggle-slider"></span>
          </label>
          <span className="toggle-label">
            {plan.status === 'Published' ? 'Published' : 'Draft'}
          </span>
        </div>
        <div className="plan-action-buttons">
          <button className="action-btn" onClick={onEdit} title="Edit">
            <Edit size={16} />
          </button>
        </div>
      </div>
    </div>
  )
}

// Plan List Item Component (List View)
const PlanListItem = ({ plan, onEdit, onDuplicate, onTogglePublish }) => {
  const isPublished = (plan.status || '').toLowerCase() === 'published'
  return (
    <div className={`plan-list-item ${isPublished ? 'status-published' : 'status-draft'}`} onClick={onEdit}>
      <div className="plan-list-header">
        <div className="plan-list-main">
          <div className="plan-list-avatar-wrapper">
            {plan.thumbnail ? (
              <img
                src={plan.thumbnail}
                alt={plan.title || 'Plan thumbnail'}
                className="plan-list-avatar-image"
                onError={(e) => {
                  // If image fails to load (bad URL, DNS error, etc.), fall back to placeholder
                  e.currentTarget.style.display = 'none';
                  const parent = e.currentTarget.parentElement;
                  if (parent) {
                    // Clear thumbnail so React will render the SVG placeholder next render
                    // (this assumes parent handler updates plan state when needed)
                  }
                }}
              />
            ) : (
              <div className="plan-list-avatar-placeholder" aria-hidden="true">
                <svg
                  width="24"
                  height="24"
                  viewBox="0 0 32 32"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <defs>
                    <linearGradient id="planListIconGradient" x1="0" y1="0" x2="1" y2="1">
                      <stop offset="0%" stopColor="#22c55e" />
                      <stop offset="100%" stopColor="#14b8a6" />
                    </linearGradient>
                  </defs>
                  <rect
                    x="6"
                    y="8"
                    width="17"
                    height="18"
                    rx="3"
                    fill="#ffffff"
                    stroke="url(#planListIconGradient)"
                    strokeWidth="1.5"
                  />
                  <circle cx="12" cy="13" r="1.3" fill="#22c55e" />
                  <rect x="15" y="12.2" width="7" height="1.5" rx="0.75" fill="#9ca3af" />
                  <circle cx="12" cy="17" r="1.3" fill="#22c55e" />
                  <rect x="15" y="16.2" width="6" height="1.5" rx="0.75" fill="#9ca3af" />
                  <circle cx="12" cy="21" r="1.3" fill="#22c55e" />
                  <rect x="15" y="20.2" width="5" height="1.5" rx="0.75" fill="#9ca3af" />
                </svg>
              </div>
            )}
          </div>
          <div className="plan-list-info">
            <h3 className="plan-list-title">{plan.title}</h3>
            <div className="plan-list-meta">
              <span className="plan-card-category">{plan.category}</span>
              <span>•</span>
              <span>{plan.activities.length} activities</span>
              <span>•</span>
              <span>{plan.duration} min</span>
            </div>
          </div>
        </div>
        <div className="plan-list-stats">
          <div className="plan-list-stat">
            <div className="plan-stat-value">{formatPriceDisplay(plan.price)} EGP</div>
            <div className="plan-stat-label">Price</div>
          </div>
          <div className="plan-list-stat">
            <div className="plan-stat-value">{plan.capacity}</div>
            <div className="plan-stat-label">Capacity</div>
          </div>
          <div className="plan-list-stat">
            <div className="plan-stat-value">{plan.bookings}</div>
            <div className="plan-stat-label">Bookings</div>
          </div>
          <div className="plan-action-buttons" onClick={(e) => e.stopPropagation()}>
            <label className="toggle-switch">
              <input
                type="checkbox"
                checked={plan.status === 'Published'}
                onChange={onTogglePublish}
              />
              <span className="toggle-slider"></span>
            </label>
            <button className="action-btn" onClick={onEdit} title="Edit">
              <Edit size={16} />
            </button>
          </div>
        </div>
      </div>
    </div>
  )
}

// Plan Editor Modal Component
const PlanEditorModal = ({ plan, onClose, onSave }) => {
  const [formData, setFormData] = useState(
    plan || {
      title: '',
      category: 'Friends',
      description: '',
      price: 0,
      duration: 0,
      capacity: 2,
      status: 'Draft',
      isPublic: true,
      activities: [],
    }
  )

  const [activities, setActivities] = useState(plan?.activities || [])

  const handleAddActivity = () => {
    const newActivity = {
      id: activities.length + 1,
      vendorId: null,
      vendorName: '',
      type: '',
      duration: 60,
      price: 0,
      time: '',
      order: activities.length + 1,
    }
    setActivities([...activities, newActivity])
  }

  const handleRemoveActivity = (activityId) => {
    setActivities(activities.filter((a) => a.id !== activityId))
  }

  const handleActivityChange = (activityId, field, value) => {
    setActivities(
      activities.map((a) => {
        if (a.id === activityId) {
          const updatedActivity = { ...a, [field]: value }
          // Auto-update vendor details when vendor changes
          if (field === 'vendorId') {
            const vendor = mockVendors.find((v) => v.id === parseInt(value))
            if (vendor) {
              updatedActivity.vendorName = vendor.name
              updatedActivity.type = vendor.type
            }
          }
          return updatedActivity
        }
        return a
      })
    )
  }

  // Calculate total price and duration
  const totalPrice = activities.reduce((sum, a) => sum + (parseFloat(a.price) || 0), 0)
  const totalDuration = activities.reduce((sum, a) => sum + (parseInt(a.duration) || 0), 0)

  const handleSubmit = (e) => {
    e.preventDefault()
    const planData = {
      ...formData,
      activities: activities.map((a, index) => ({ ...a, order: index + 1 })),
      price: totalPrice,
      duration: totalDuration,
    }
    onSave(planData)
  }

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal plan-modal" onClick={(e) => e.stopPropagation()}>
        <div className="modal-header">
          <h2 className="modal-title">{plan ? 'Edit Plan' : 'Create New Plan'}</h2>
          <button className="modal-close" onClick={onClose}>
            <X size={20} />
          </button>
        </div>
        <form onSubmit={handleSubmit}>
          <div className="modal-body">
            <div className="form-row">
              <div className="form-group">
                <label className="form-label">Plan Title *</label>
                <input
                  type="text"
                  className="form-input"
                  value={formData.title}
                  onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                  required
                />
              </div>
              <div className="form-group">
                <label className="form-label">Category *</label>
                <select
                  className="form-select"
                  value={formData.category}
                  onChange={(e) => setFormData({ ...formData, category: e.target.value })}
                  required
                >
                  {PLAN_CATEGORIES.map((cat) => (
                    <option key={cat} value={cat}>
                      {cat}
                    </option>
                  ))}
                </select>
              </div>
            </div>

            <div className="form-group">
              <label className="form-label">Description</label>
              <textarea
                className="form-textarea"
                value={formData.description}
                onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                placeholder="Brief description of this hangout plan..."
              />
            </div>

            <div className="form-row">
              <div className="form-group">
                <label className="form-label">Capacity</label>
                <input
                  type="number"
                  className="form-input"
                  value={formData.capacity}
                  onChange={(e) => setFormData({ ...formData, capacity: parseInt(e.target.value) })}
                  min="1"
                />
              </div>
              <div className="form-group">
                <label className="form-label">Visibility</label>
                <select
                  className="form-select"
                  value={formData.isPublic ? 'public' : 'private'}
                  onChange={(e) =>
                    setFormData({ ...formData, isPublic: e.target.value === 'public' })
                  }
                >
                  <option value="public">Public</option>
                  <option value="private">Private</option>
                </select>
              </div>
            </div>

            {/* Activities Editor */}
            <div className="activities-editor">
              <div className="activities-editor-header">
                <h3 className="activities-editor-title">Activities (1-4)</h3>
                <button
                  type="button"
                  className="add-activity-btn"
                  onClick={handleAddActivity}
                  disabled={activities.length >= 4}
                >
                  <Plus size={16} />
                  Add Activity
                </button>
              </div>

              <div className="activities-list">
                {activities.map((activity, index) => (
                  <div key={activity.id} className="activity-editor-item">
                    <div className="activity-editor-header">
                      <GripVertical size={20} className="drag-handle" />
                      <div className="activity-order-number">{index + 1}</div>
                      <span className="activity-title">Activity {index + 1}</span>
                      <button
                        type="button"
                        className="remove-activity-btn"
                        onClick={() => handleRemoveActivity(activity.id)}
                      >
                        <Trash2 size={16} />
                      </button>
                    </div>
                    <div className="activity-editor-fields">
                      <div className="form-group">
                        <label className="form-label">Vendor</label>
                        <select
                          className="form-select"
                          value={activity.vendorId || ''}
                          onChange={(e) =>
                            handleActivityChange(activity.id, 'vendorId', e.target.value)
                          }
                        >
                          <option value="">Select Vendor</option>
                          {mockVendors.map((vendor) => (
                            <option key={vendor.id} value={vendor.id}>
                              {vendor.name} ({vendor.type})
                            </option>
                          ))}
                        </select>
                      </div>
                      <div className="form-group">
                        <label className="form-label">Duration (min)</label>
                        <input
                          type="number"
                          className="form-input"
                          value={activity.duration}
                          onChange={(e) =>
                            handleActivityChange(activity.id, 'duration', e.target.value)
                          }
                        />
                      </div>
                      <div className="form-group">
                        <label className="form-label">Price (EGP)</label>
                        <input
                          type="number"
                          className="form-input"
                          value={activity.price}
                          onChange={(e) =>
                            handleActivityChange(activity.id, 'price', e.target.value)
                          }
                        />
                      </div>
                    </div>
                  </div>
                ))}
              </div>

              {/* Auto Price Calculator */}
              <div className="price-calculator">
                <div className="price-calculator-header">
                  <span className="calculator-label">Total Plan Value</span>
                  <span className="calculator-total">{formatPriceDisplay(totalPrice)} EGP</span>
                </div>
                <div className="calculator-breakdown">
                  Duration: {totalDuration} minutes • {activities.length} activities
                </div>
              </div>
            </div>
          </div>
          <div className="modal-footer">
            <button type="button" className="btn-secondary" onClick={onClose}>
              Cancel
            </button>
            <button type="submit" className="btn-primary">
              {plan ? 'Save Changes' : 'Create Plan'}
            </button>
          </div>
        </form>
      </div>
    </div>
  )
}

export default Plans
